import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Copy, Check, Sparkles, Users, RefreshCw, User, CalendarClock, Send, AlertTriangle, Download, FileText, FileSpreadsheet, ThumbsUp, ThumbsDown, Gift, Loader2, Mail, Music, Package, CheckCircle2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useNoIndex } from "@/lib/useNoIndex";
import { t, nameForm } from "@/lib/i18n";
import { ReportDialog } from "@/components/ReportDialog";
import { Footer } from "@/components/Footer";
import { PurchaseSection } from "@/components/PurchaseSection";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TODAY_STR = fmtDateInput(new Date());
const MAX_STR = fmtDateInput(new Date(Date.now() + 30 * 86400000));
const fmtFi = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fi-FI", { timeZone: "Europe/Helsinki", day: "2-digit", month: "2-digit", year: "numeric" }) : "";

export default function AdminPage() {
  useNoIndex();
  const { slug, token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newDate, setNewDate] = useState(MAX_STR);
  const [extendOpen, setExtendOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const [deletingNow, setDeletingNow] = useState(false);
  const [collectionDeleted, setCollectionDeleted] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [justPurchased, setJustPurchased] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState("");
  const [addonEmail, setAddonEmail] = useState("");
  const [addonSent, setAddonSent] = useState({ audio: false, print: false });
  const [releasing, setReleasing] = useState(false);
  const [giftCopied, setGiftCopied] = useState(false);
  const [translatingDe, setTranslatingDe] = useState(false);
  const [nfGenitive, setNfGenitive] = useState("");
  const [nfAllative, setNfAllative] = useState("");
  const [nfInit, setNfInit] = useState(false);
  const [savingNf, setSavingNf] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/admin/${slug}/${token}`);
      setData(res.data);
      if (res.data.feedback_thumb) setThumb(res.data.feedback_thumb);
      if (res.data.feedback_text) { setFeedbackText(res.data.feedback_text); setFeedbackSent(true); }
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  // Initialise the Finnish name-form editor once, from the loaded collection.
  useEffect(() => {
    if (data && !nfInit) {
      const fi = (data.name_forms && data.name_forms.fi) || {};
      setNfGenitive(fi.genitive || "");
      setNfAllative(fi.allative || "");
      setNfInit(true);
    }
  }, [data, nfInit]);

  const saveNameForms = async () => {
    setSavingNf(true);
    try {
      const { data: res } = await api.patch(`/admin/${slug}/${token}/name-forms`, {
        genitive: nfGenitive,
        allative: nfAllative,
      });
      setData((d) => ({ ...d, name_forms: res.name_forms, name_forms_confirmed: true }));
      toast.success(t("nameForms.saved"));
    } catch {
      toast.error(t("nameForms.saveFailed"));
    } finally {
      setSavingNf(false);
    }
  };

  // Handle return from Stripe Checkout. Never trust the redirect as proof of payment:
  // poll the server (which verifies with Stripe) until is_unlocked becomes true.
  useEffect(() => {
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (payment === "cancel") {
      toast.info(t("admin.toast.paymentCancelled"));
      setSearchParams({}, { replace: true });
      return;
    }
    if (payment === "success" && sessionId) {
      setConfirmingPayment(true);
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: st } = await api.get(`/payments/status/${sessionId}`);
          if (st.buyer_email) setBuyerEmail(st.buyer_email);
          if (st.is_unlocked) {
            setConfirmingPayment(false);
            setJustPurchased(true);
            if (st.buyer_email) setAddonEmail(st.buyer_email);
            await load();
            toast.success(t("admin.toast.paymentConfirmed"));
            setSearchParams({}, { replace: true });
            return;
          }
        } catch (e) { /* keep polling */ }
        if (tries < 30) {
          setTimeout(poll, 2000);
        } else {
          setConfirmingPayment(false);
          toast.error(t("admin.toast.paymentSlow"));
        }
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleStatus = async (open) => {
    try {
      await api.patch(`/admin/${slug}/${token}/status`, { is_open: open });
      await load();
      toast.success(open ? t("admin.toast.opened") : t("admin.toast.closed"));
    } catch {
      toast.error(t("admin.toast.statusFailed"));
    }
  };

  const extendDeadline = async () => {
    try {
      await api.patch(`/admin/${slug}/${token}/extend`, { closes_at_date: newDate });
      setExtendOpen(false);
      await load();
      toast.success(t("admin.toast.extended"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.toast.extendFailed"));
    }
  };

  const toggleConsent = async (value) => {
    try {
      await api.patch(`/admin/${slug}/${token}/consent`, { marketing_consent: value });
      setData((d) => ({ ...d, marketing_consent: value }));
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  const deleteNow = async () => {
    setDeletingNow(true);
    try {
      await api.delete(`/admin/${slug}/${token}/delete-now`);
      setCollectionDeleted(true);
    } catch {
      toast.error(t("common.deleteFailed"));
    } finally {
      setDeletingNow(false);
    }
  };

  const rebuildBook = async () => {
    setRebuilding(true);
    try {
      await api.post(`/admin/${slug}/${token}/rebuild-book`);
      await load();
      toast.success(t("admin.toast.bookRebuilt"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.toast.rebuildFailed"));
    } finally {
      setRebuilding(false);
    }
  };

  const sendFeedback = async (choice) => {
    setThumb(choice);
    try {
      await api.post(`/admin/${slug}/${token}/feedback`, { thumb: choice, feedback_text: "" });
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  const sendFeedbackText = async () => {
    try {
      await api.post(`/admin/${slug}/${token}/feedback`, { thumb, feedback_text: feedbackText });
      setFeedbackSent(true);
      toast.success(t("admin.toast.feedbackThanks"));
    } catch {
      toast.error(t("admin.toast.feedbackFailed"));
    }
  };

  const deleteAnswer = async (id) => {
    try {
      await api.delete(`/admin/${slug}/${token}/answers/${id}`);
      setData((d) => ({
        ...d,
        answers: d.answers.filter((a) => a.id !== id),
        answer_count: d.answer_count - 1,
      }));
      toast.success(t("admin.toast.answerDeleted"));
    } catch {
      toast.error(t("common.deleteFailed"));
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/admin/${slug}/${token}/portrait`);
      await load();
      toast.success(t("admin.toast.portraitCreated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.toast.portraitFailed"));
    } finally {
      setGenerating(false);
    }
  };

  const translateDe = async () => {
    setTranslatingDe(true);
    try {
      await api.post(`/admin/${slug}/${token}/portrait/de`);
      await load();
      toast.success(t("admin.toast.germanCreated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.toast.germanFailed"));
    } finally {
      setTranslatingDe(false);
    }
  };

  const releaseGift = async () => {
    setReleasing(true);
    try {
      const { data: res } = await api.post(`/admin/${slug}/${token}/release`);
      await load();
      if (res.already) {
        toast.info(t("admin.toast.releaseAlready"));
      } else {
        toast.success(t("admin.toast.releaseDone", { n: res.recipients }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.toast.releaseFailed"));
    } finally {
      setReleasing(false);
    }
  };

  const sendAddonInterest = async (addon) => {
    if (!addonEmail) { toast.error(t("admin.toast.addonEmailMissing")); return; }
    try {
      await api.post(`/collections/${slug}/addon-interest`, { addon, email: addonEmail });
      setAddonSent((s) => ({ ...s, [addon]: true }));
      toast.success(t("admin.toast.addonThanks"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.saveFailed"));
    }
  };

  const copyPortrait = async () => {
    await navigator.clipboard.writeText(data.portrait_text);
    setCopied(true);
    toast.success(t("admin.toast.textCopied"));
    setTimeout(() => setCopied(false), 1800);
  };

  const resendLinks = async () => {
    setResending(true);
    try {
      const { data: res } = await api.post(`/admin/${slug}/${token}/resend`);
      if (res.email_sent) {
        toast.success(t("admin.toast.linksSent", { email: res.creator_email }));
      } else {
        toast.error(t("admin.toast.emailFailed"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.toast.sendFailed"));
    } finally {
      setResending(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[#7A6B63]">{t("common.loading")}</div>;
  if (error)
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center">
        <div>
          <h1 className="font-serif text-3xl text-[#2C221E]">{t("admin.error.title")}</h1>
          <p className="mt-2 text-[#7A6B63]">{t("admin.error.body")}</p>
        </div>
      </div>
    );

  if (collectionDeleted)
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center" data-testid="collection-deleted-screen">
        <div>
          <h1 className="font-serif text-3xl text-[#2C221E]">{t("admin.deletedScreen.title")}</h1>
          <p className="mt-2 text-[#7A6B63]">{t("admin.deletedScreen.body")}</p>
        </div>
      </div>
    );

  const { person_name, is_open, closed, closes_at, creator_email, marketing_consent, needs_regeneration, answers, questions, portrait_text, answer_count, answers_deleted, retention_days_left, regens_left, needs_rebuild, book_built, portrait_built, is_unlocked, is_released, gift_url, contributor_email_count, has_second_language, portrait_de_built, portrait_text_de } = data;
  const origin = window.location.origin;
  const shareUrl = `${origin}/v/${slug}`;

  const isAnon = (a) => a.is_anonymous || !a.respondent_name;
  const namedList = [...new Set(answers.filter((a) => !isAnon(a)).map((a) => a.respondent_name.trim()))]
    .sort((x, y) => x.localeCompare(y, "fi"));
  const anonCount = answers.filter(isAnon).length;
  const dl = (ext) => `${API}/admin/${slug}/${token}/${ext}`;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
        <div className="flex items-center gap-2 text-[#C86D51] mb-3">
          <Sparkles className="w-5 h-5" />
          <span className="text-sm font-semibold tracking-wide uppercase">{t("admin.eyebrow")}</span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-[#2C221E]">
          {(() => {
            const gen = nameForm(data.name_forms, "fi", "genitive");
            return t(gen ? "admin.heading" : "admin.heading.fb", { name: gen || person_name });
          })()}
        </h1>

        <div className="mt-5 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-5 space-y-4" data-testid="admin-name-forms">
          <div>
            <p className="text-base font-medium text-[#2C221E]">{t("nameForms.sectionTitle")}</p>
            <p className="text-sm text-[#7A6B63] mt-0.5">{t("nameForms.sectionHelp")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nf-genitive" className="text-[#2C221E]">{t("nameForms.genitiveLabel")}</Label>
            <Input
              id="nf-genitive"
              value={nfGenitive}
              onChange={(e) => setNfGenitive(e.target.value)}
              className="h-11 rounded-xl border-[#E8DEC2] bg-white"
              data-testid="admin-fi-genitive-input"
            />
            <p className="text-sm font-medium text-[#B35A3E]" data-testid="admin-fi-genitive-preview">
              {nfGenitive.trim()
                ? t("nameForms.genitivePreview", { name: nfGenitive.trim() })
                : t("nameForms.genitivePreviewFb", { name: person_name })}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nf-allative" className="text-[#2C221E]">{t("nameForms.allativeLabel")}</Label>
            <Input
              id="nf-allative"
              value={nfAllative}
              onChange={(e) => setNfAllative(e.target.value)}
              className="h-11 rounded-xl border-[#E8DEC2] bg-white"
              data-testid="admin-fi-allative-input"
            />
            <p className="text-sm font-medium text-[#B35A3E]" data-testid="admin-fi-allative-preview">
              {nfAllative.trim()
                ? t("nameForms.allativePreview", { name: nfAllative.trim() })
                : t("nameForms.allativePreviewFb", { name: person_name })}
            </p>
          </div>
          <Button
            onClick={saveNameForms}
            disabled={savingNf}
            className="h-10 px-4 rounded-xl bg-[#C86D51] hover:bg-[#B35A3E] text-white"
            data-testid="admin-save-name-forms"
          >
            {savingNf ? t("common.saving") : t("nameForms.save")}
          </Button>
        </div>

        {confirmingPayment && (
          <div className="mt-6 flex items-center gap-3 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-5" data-testid="payment-confirming">
            <Loader2 className="w-5 h-5 text-[#C86D51] animate-spin shrink-0" />
            <p className="text-sm text-[#4A3E39]">{t("admin.paymentConfirming")}</p>
          </div>
        )}

        {justPurchased && is_unlocked && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#EAF1EA] border border-[#CBE0CB] p-5" data-testid="payment-success">
            <CheckCircle2 className="w-5 h-5 text-[#3f6047] shrink-0 mt-0.5" />
            <p className="text-sm text-[#2f4a37]">{t("admin.paymentSuccess")}</p>
          </div>
        )}
        {is_unlocked && data.invoice_available && (
          <div className="mt-4 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-4" data-testid="admin-invoice-block">
            <p className="text-sm text-[#4A3E39]">
              {t("admin.vatNumberLabel")}: <strong>{data.vat_number || "—"}</strong> · {t("admin.reverseChargeNote")}
            </p>
            <a
              href={dl("invoice.pdf")}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center h-10 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium"
              data-testid="download-invoice"
            >
              <Download className="w-4 h-4 mr-2" /> {t("admin.invoiceDownload")}
            </a>
          </div>
        )}
        {creator_email && (
          <div className="mt-3">
            <Button
              onClick={resendLinks}
              disabled={resending}
              variant="outline"
              className="h-10 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"
              data-testid="resend-links-button"
            >
              <Send className="w-4 h-4 mr-2" />
              {resending ? t("common.sending") : t("admin.resendLinks")}
            </Button>
          </div>
        )}

        {answers_deleted ? (
          <div className="mt-6 rounded-2xl bg-[#F3EDE6] border border-[#E8DEC2] p-5" data-testid="retention-deleted-note">
            <p className="text-[#4A3E39]">{t("admin.retentionDeleted")}</p>
          </div>
        ) : (
          retention_days_left != null && (
            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-5" data-testid="retention-note">
              <CalendarClock className="w-5 h-5 text-[#C86D51] shrink-0 mt-0.5" />
              <p className="text-sm text-[#4A3E39] leading-relaxed">
                {t("admin.retentionPre")} <strong>{t("admin.retentionDays", { n: retention_days_left })}</strong> {t("admin.retentionPost")}
              </p>
            </div>
          )
        )}

        {/* Share + QR */}
        <div className="mt-6 rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm" data-testid="share-card">
          <h2 className="font-serif text-xl text-[#2C221E] mb-3">{t("admin.share.title")}</h2>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <img src={`${API}/v/${slug}/qr.png`} alt={t("admin.share.qrAlt")} className="w-32 h-32 rounded-xl border border-[#E8DEC2]" data-testid="share-qr" />
            <div className="flex-1 min-w-0 w-full">
              <div className="rounded-xl bg-[#FDFBF7] border border-[#E8DEC2] px-3 py-3 text-sm text-[#4A3E39] break-all font-mono" data-testid="share-url">{shareUrl}</div>
              <div className="mt-2 flex gap-2">
                <Button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success(t("common.linkCopied")); }} variant="outline" className="h-10 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]" data-testid="copy-share-button">
                  <Copy className="w-4 h-4 mr-2" /> {t("admin.share.copy")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats + toggle */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-[#7A6B63] text-sm"><Users className="w-4 h-4" /> {t("admin.stats.answers")}</div>
            <div className="mt-1 font-serif text-3xl text-[#2C221E]" data-testid="answer-count">{answer_count}</div>
          </div>
          <div className="rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#7A6B63] text-sm">{t("admin.stats.status")}</span>
              <Switch checked={is_open} onCheckedChange={toggleStatus} data-testid="status-toggle" />
            </div>
            <div className="mt-1 font-serif text-2xl text-[#2C221E]" data-testid="status-label">
              {closed ? t("admin.status.closed") : t("admin.status.open")}
            </div>
          </div>
        </div>

        {/* Deadline + extend */}
        <div className="mt-3 rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 text-[#4A3E39]">
            <CalendarClock className="w-5 h-5 text-[#C86D51]" />
            <span className="text-sm">
              {t("admin.closesLabel")} <strong data-testid="closes-at-label">{fmtFi(closes_at)}</strong>
            </span>
          </div>
          <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="h-11 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"
                data-testid="extend-button"
              >
                {t("admin.extend")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif">{t("admin.extendTitle")}</DialogTitle>
                <DialogDescription>
                  {t("admin.extendDesc")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="new-date" className="text-[#2C221E]">{t("admin.extendDateLabel")}</Label>
                <Input
                  id="new-date"
                  type="date"
                  value={newDate}
                  min={TODAY_STR}
                  max={MAX_STR}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7]"
                  data-testid="extend-date-input"
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost" className="rounded-xl">{t("common.cancel")}</Button>
                </DialogClose>
                <Button
                  onClick={extendDeadline}
                  className="bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
                  data-testid="extend-confirm-button"
                >
                  {t("common.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Generate portrait */}
        <div className="mt-6 rounded-3xl border border-[#E8DEC2] bg-white p-6 shadow-sm">
          {needs_regeneration && (
            <div className="mb-4 flex items-start gap-3 rounded-2xl bg-[#FBECE7] border border-[#E9C9BC] p-4" data-testid="regen-note">
              <AlertTriangle className="w-5 h-5 text-[#B35A3E] shrink-0 mt-0.5" />
              <p className="text-sm text-[#8a4b38]">{t("admin.regenNote")}</p>
            </div>
          )}
          {needs_rebuild && (
            <div className="mb-4 flex items-start justify-between gap-3 rounded-2xl bg-[#FBECE7] border border-[#E9C9BC] p-4" data-testid="rebuild-note">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#B35A3E] shrink-0 mt-0.5" />
                <p className="text-sm text-[#8a4b38]">{t("admin.rebuildNote")}</p>
              </div>
              <Button onClick={rebuildBook} disabled={rebuilding || answers_deleted} variant="outline" className="h-9 shrink-0 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-white" data-testid="rebuild-book-button">
                {rebuilding ? t("admin.rebuilding") : t("admin.rebuildButton")}
              </Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-serif text-2xl text-[#2C221E]">{t("admin.portraitTitle")}</h2>
              <p className="text-sm text-[#7A6B63]">{t("admin.portraitSubtitle")}</p>
            </div>
            <Button
              onClick={generate}
              disabled={generating || answer_count === 0 || answers_deleted || regens_left === 0}
              className="h-12 px-5 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
              data-testid="generate-portrait-button"
            >
              {generating ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {t("admin.generating")}</>
              ) : portrait_built ? (
                <><RefreshCw className="w-4 h-4 mr-2" /> {t("admin.regenerate", { n: regens_left })}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> {t("admin.generate")}</>
              )}
            </Button>
          </div>

          {answer_count === 0 && (
            <p className="mt-4 text-sm text-[#7A6B63]">{t("admin.noAnswersYet")}</p>
          )}

          {portrait_text && (
            <div className="mt-5">
              <div
                className="rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-6 text-[#2C221E] text-[17px] leading-loose whitespace-pre-wrap font-serif"
                data-testid="portrait-text"
              >
                {portrait_text}
              </div>
              <Button
                onClick={copyPortrait}
                variant="outline"
                className="mt-3 h-11 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"
                data-testid="copy-portrait-button"
              >
                {copied ? <><Check className="w-4 h-4 mr-2" /> {t("common.copied")}</> : <><Copy className="w-4 h-4 mr-2" /> {t("common.copy")}</>}
              </Button>

              <div className="mt-6 pt-6 border-t border-[#E8DEC2]" data-testid="credits-section">
                <h3 className="font-serif text-xl text-[#2C221E] mb-2">{t("admin.creditsTitle")}</h3>
                {namedList.length === 0 ? (
                  <p className="text-[#4A3E39]" data-testid="credits-anon-only">
                    {t("admin.creditsAnonOnly", { n: anonCount })}
                  </p>
                ) : (
                  <div className="text-[#4A3E39]">
                    <p data-testid="credits-names">{namedList.join(", ")}</p>
                    {anonCount > 0 && (
                      <p className="mt-1 text-[#7A6B63]" data-testid="credits-anon-line">
                        {t("admin.creditsAnonLine", { n: anonCount })}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-[#E8DEC2]" data-testid="feedback-section">
                <h3 className="font-serif text-xl text-[#2C221E] mb-3">{t("admin.feedbackTitle")}</h3>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant={thumb === "up" ? "default" : "outline"}
                    onClick={() => sendFeedback("up")}
                    className={`h-11 rounded-xl ${thumb === "up" ? "bg-[#C86D51] hover:bg-[#B35A3E] text-white" : "border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"}`}
                    data-testid="thumb-up-button"
                  >
                    <ThumbsUp className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant={thumb === "down" ? "default" : "outline"}
                    onClick={() => sendFeedback("down")}
                    className={`h-11 rounded-xl ${thumb === "down" ? "bg-[#C86D51] hover:bg-[#B35A3E] text-white" : "border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"}`}
                    data-testid="thumb-down-button"
                  >
                    <ThumbsDown className="w-4 h-4" />
                  </Button>
                </div>
                {thumb && !feedbackSent && (
                  <div className="mt-4 space-y-3">
                    <Textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder={t("admin.feedbackPlaceholder")}
                      className="min-h-[80px] rounded-xl border-[#E8DEC2] bg-[#FDFBF7]"
                      data-testid="feedback-text-input"
                    />
                    <Button onClick={sendFeedbackText} className="h-11 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl" data-testid="feedback-submit-button">
                      {t("common.send")}
                    </Button>
                  </div>
                )}
                {feedbackSent && (
                  <p className="mt-3 text-sm text-[#3f6047]" data-testid="feedback-confirmation">{t("admin.feedbackThanks")}</p>
                )}
              </div>

              {has_second_language && (
                <div className="mt-6 pt-6 border-t border-[#E8DEC2]" data-testid="german-section">
                  <h3 className="font-serif text-xl text-[#2C221E] mb-1">{t("admin.germanTitle")}</h3>
                  <p className="text-sm text-[#7A6B63] mb-3">{t("admin.germanNote")}</p>
                  <Button
                    onClick={translateDe}
                    disabled={translatingDe}
                    variant="outline"
                    className="h-11 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]"
                    data-testid="create-german-button"
                  >
                    {translatingDe ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {t("admin.translating")}</>
                    ) : portrait_de_built ? (
                      <><RefreshCw className="w-4 h-4 mr-2" /> {t("admin.regenGerman")}</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> {t("admin.createGerman")}</>
                    )}
                  </Button>
                  {portrait_de_built && portrait_text_de && (
                    <div className="mt-4">
                      <div
                        className="rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-6 text-[#2C221E] text-[17px] leading-loose whitespace-pre-wrap font-serif"
                        data-testid="portrait-text-de"
                      >
                        {portrait_text_de}
                      </div>
                      <a
                        href={dl("portrait_de.pdf")}
                        className="mt-3 inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium"
                        data-testid="download-portrait-de-pdf-inline"
                      >
                        <Download className="w-4 h-4 mr-2" /> {t("admin.downloadPortraitDePdf")}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Paywall preview + purchase (portrait built, not yet paid) */}
        {portrait_built && !is_unlocked && book_built && !answers_deleted && (
          <PurchaseSection slug={slug} token={token} />
        )}

        {/* Gift release + downloads live below once paid */}
        {is_unlocked && (
          <div className="mt-6 rounded-3xl border border-[#E8DEC2] bg-white p-6 shadow-sm" data-testid="gift-release-section">
            <div className="flex items-center gap-2 text-[#C86D51] mb-1">
              <Gift className="w-5 h-5" />
              <span className="text-sm font-semibold tracking-wide uppercase">{t("admin.giftEyebrow")}</span>
            </div>
            <h2 className="font-serif text-2xl text-[#2C221E]">{t("admin.giftTitle")}</h2>
            {gift_url && (
              <div className="mt-3">
                <div className="rounded-xl bg-[#FDFBF7] border border-[#E8DEC2] px-3 py-3 text-sm text-[#4A3E39] break-all font-mono" data-testid="gift-url">{gift_url}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    onClick={() => { navigator.clipboard.writeText(gift_url); setGiftCopied(true); toast.success(t("admin.toast.giftLinkCopied")); setTimeout(() => setGiftCopied(false), 1800); }}
                    variant="outline" className="h-10 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]" data-testid="copy-gift-button"
                  >
                    {giftCopied ? <><Check className="w-4 h-4 mr-2" /> {t("common.copied")}</> : <><Copy className="w-4 h-4 mr-2" /> {t("admin.copyGiftLink")}</>}
                  </Button>
                  <a href={gift_url} target="_blank" rel="noreferrer" className="inline-flex items-center h-10 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="open-gift-link">
                    {t("admin.openGiftPage")}
                  </a>
                </div>
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-[#F0E8DA]">
              {is_released ? (
                <p className="text-sm text-[#3f6047] flex items-center gap-2" data-testid="release-done">
                  <CheckCircle2 className="w-4 h-4" /> {t("admin.releaseDone")}
                </p>
              ) : (
                <>
                  <p className="text-sm text-[#4A3E39] mb-3">
                    {t("admin.releaseInfo")}
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button className="h-11 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl" disabled={releasing} data-testid="release-gift-button">
                        <Send className="w-4 h-4 mr-2" /> {t("admin.releaseButton")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("admin.releaseConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("admin.releaseConfirmDesc", { n: contributor_email_count })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={releaseGift} disabled={releasing} className="bg-[#C86D51] hover:bg-[#B35A3E]" data-testid="confirm-release-gift">
                          {releasing ? t("admin.releasing") : t("admin.releaseNow")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        )}

        {/* Coming-soon add-ons (notification signup only) */}
        {is_unlocked && justPurchased && (
          <div className="mt-6 rounded-3xl border border-[#E8DEC2] bg-white p-6 shadow-sm" data-testid="addons-section">
            <h2 className="font-serif text-2xl text-[#2C221E]">{t("admin.addonsTitle")}</h2>
            <p className="text-sm text-[#7A6B63] mt-1">
              {t("admin.addonsSubtitle")}
            </p>
            <div className="mt-4">
              <Label htmlFor="addon-email" className="text-[#2C221E] text-sm">{t("admin.addonEmailLabel")}</Label>
              <Input
                id="addon-email" type="email" value={addonEmail}
                onChange={(e) => setAddonEmail(e.target.value)}
                className="h-11 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] mt-1"
                data-testid="addon-email-input"
              />
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-[#E8DEC2] bg-[#FDFBF7] p-4">
                <div className="flex items-center gap-2 text-[#2C221E] font-medium"><Music className="w-4 h-4 text-[#C86D51]" /> {t("admin.addonAudioTitle")}</div>
                <p className="text-sm text-[#7A6B63] mt-1">{t("admin.addonAudioDesc")}</p>
                <Button onClick={() => sendAddonInterest("audio")} disabled={addonSent.audio} variant="outline" className="mt-3 h-10 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]" data-testid="addon-audio-button">
                  {addonSent.audio ? <><Check className="w-4 h-4 mr-2" /> {t("admin.addonNotified")}</> : t("admin.addonNotifyMe")}
                </Button>
              </div>
              <div className="rounded-2xl border border-[#E8DEC2] bg-[#FDFBF7] p-4">
                <div className="flex items-center gap-2 text-[#2C221E] font-medium"><Package className="w-4 h-4 text-[#C86D51]" /> {t("admin.addonPrintTitle")}</div>
                <p className="text-sm text-[#7A6B63] mt-1">{t("admin.addonPrintDesc")}</p>
                <Button onClick={() => sendAddonInterest("print")} disabled={addonSent.print} variant="outline" className="mt-3 h-10 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]" data-testid="addon-print-button">
                  {addonSent.print ? <><Check className="w-4 h-4 mr-2" /> {t("admin.addonNotified")}</> : t("admin.addonNotifyMe")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Answers list */}
        <div className="mt-6">
          <h2 className="font-serif text-2xl text-[#2C221E] mb-3">{t("admin.answersTitle")}</h2>
          {answers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E8DEC2] bg-white p-6 text-center text-[#7A6B63]">
              {t("admin.noAnswers")}
            </div>
          ) : (
            <div className="space-y-3" data-testid="answers-list">
              <AnimatePresence>
                {answers.map((a) => (
                  <motion.div
                    key={a.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, height: 0 }}
                    className="rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm"
                    data-testid={`answer-item-${a.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 text-[#2C221E] font-semibold">
                          <User className="w-4 h-4 text-[#C86D51]" />
                          {isAnon(a) ? t("admin.anonFriend") : a.respondent_name}
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${isAnon(a) ? "bg-[#F3EDE6] text-[#7A6B63]" : "bg-[#EAF1EA] text-[#3f6047]"}`}
                          data-testid={`anon-badge-${a.id}`}
                        >
                          {isAnon(a) ? t("admin.badgeAnon") : t("admin.badgeNamed")}
                        </span>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-[#B84A39] hover:bg-[#FBECE7] rounded-lg"
                            data-testid={`delete-answer-button-${a.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-2xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("admin.deleteAnswerTitle")}</AlertDialogTitle>
                            <AlertDialogDescription>{t("admin.deleteAnswerDesc")}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteAnswer(a.id)}
                              className="bg-[#B84A39] hover:bg-[#9d3e30]"
                              data-testid={`confirm-delete-${a.id}`}
                            >
                              {t("admin.delete")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <div className="space-y-3">
                      {questions.map((q) =>
                        a.answers?.[q.key] ? (
                          <div key={q.key}>
                            <p className="text-xs text-[#7A6B63] leading-relaxed">{q.text}</p>
                            <p className="text-[15px] text-[#4A3E39] leading-relaxed mt-0.5 whitespace-pre-wrap">{a.answers[q.key]}</p>
                          </div>
                        ) : null
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Downloads + delete now */}
        <div className="mt-6 rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm">
          <h2 className="font-serif text-xl text-[#2C221E] mb-3">{t("admin.downloadsTitle")}</h2>
          <div className="flex flex-wrap gap-2">
            {!answers_deleted && (
              <>
                <a href={dl("export.txt")} className="inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="download-answers-txt">
                  <FileText className="w-4 h-4 mr-2" /> {t("admin.downloadAnswersTxt")}
                </a>
                <a href={dl("export.csv")} className="inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="download-answers-csv">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> {t("admin.downloadAnswersCsv")}
                </a>
              </>
            )}
            {portrait_text && (
              <a href={dl("portrait.txt")} className="inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="download-portrait">
                <Download className="w-4 h-4 mr-2" /> {t("admin.downloadPortraitTxt")}
              </a>
            )}
            {is_unlocked && book_built && (
              <>
                <a href={dl("portrait.pdf")} className="inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="download-portrait-pdf">
                  <Download className="w-4 h-4 mr-2" /> {t("admin.downloadPortraitPdf")}
                </a>
                <a href={dl("book.pdf")} className="inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="download-book-pdf">
                  <Download className="w-4 h-4 mr-2" /> {t("admin.downloadBookPdf")}
                </a>
                {portrait_de_built && (
                  <a href={dl("portrait_de.pdf")} className="inline-flex items-center h-11 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm font-medium" data-testid="download-portrait-de-pdf">
                    <Download className="w-4 h-4 mr-2" /> {t("admin.downloadPortraitDePdf")}
                  </a>
                )}
              </>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-[#F0E8DA]">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-[#B84A39] hover:bg-[#FBECE7] rounded-xl" data-testid="delete-now-button">
                  <Trash2 className="w-4 h-4 mr-2" /> {t("admin.deleteNow")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("admin.deleteNowTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("admin.deleteNowDesc")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteNow}
                    disabled={deletingNow}
                    className="bg-[#B84A39] hover:bg-[#9d3e30]"
                    data-testid="confirm-delete-now"
                  >
                    {deletingNow ? t("admin.deletingNow") : t("admin.deletePermanently")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Marketing consent */}
        <div className="mt-6 rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <Checkbox
              id="marketing-consent"
              checked={marketing_consent}
              onCheckedChange={(v) => toggleConsent(!!v)}
              data-testid="marketing-consent-checkbox"
              className="mt-1"
            />
            <Label htmlFor="marketing-consent" className="text-[#4A3E39] text-base cursor-pointer leading-relaxed">
              {t("admin.marketingConsent")}
            </Label>
          </div>
        </div>

        {/* Report abuse */}
        <div className="mt-8 pt-6 border-t border-[#E8DEC2] flex justify-center" data-testid="admin-report-row">
          <ReportDialog slug={slug} />
        </div>
      </div>
      <Footer slug={slug} />
    </div>
  );
}
